import { getUsualTableColumns } from '@/widgets/UsualTable/model/columns';
import { Table } from '@/entities/Table';
import { SelectedPopup } from '@/entities/SelectedPopup';
import { useAppDispatch, useAppSelector } from '@/shared/lib/hooks/redux';
import { setSelectedArray } from '@/widgets/UsualTable';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Loader } from '@/shared/ui/Loader/Loader';
import s from './UsualTable.module.scss';
import { UsualTableItem } from '@/shared/model/types/table';
import { useGetUserListDataQuery, usePatchUserDataMutation } from '@/entities/User';
import { UserChangeLogModal } from '@/features/UserChangeLogModal';
import { BlockReasonModal } from '@/features/BlockReasonModal';
import { getAccountDisplayName } from '@/shared/lib/utils/accountPresentation';

export const UsualTable = () => {
  const dispatch = useAppDispatch();
  const { selectedArray } = useAppSelector((state) => state.usualTable);
  const { isLoading, data } = useGetUserListDataQuery();
  const [patchUserData] = usePatchUserDataMutation();
  const tableRef = useRef(null);
  const [logModalInfo, setLogModalInfo] = useState<{ id: number; name: string } | null>(null);
  const onSelect = useCallback(
    (data: UsualTableItem[]) => {
      dispatch(setSelectedArray(data));
    },
    [dispatch],
  );

  const handleSave = useCallback(
    async (id: number, field: string, value: string | null, blockReason?: string | null) => {
      if (data?.some((item) => item.id === id && item.accountStatus === 'deleted')) return;

      let payloadValue: string | boolean | null = value;
      const patchData: any = {};
      if (field === 'isActive') {
        payloadValue = value === 'true';
        if (blockReason) {
          patchData.blockReason = blockReason;
        }
      }
      patchData[field] = payloadValue;
      await patchUserData({ id, data: patchData }).unwrap();
    },
    [data, patchUserData],
  );

  const handleOpenLog = useCallback((userId: number, userName: string) => {
    setLogModalInfo({ id: userId, name: userName });
  }, []);

  const handleCloseLog = useCallback(() => {
    setLogModalInfo(null);
  }, []);

  const [blockTargetId, setBlockTargetId] = useState<number | null>(null);
  const [blockModalOpen, setBlockModalOpen] = useState(false);

  const onToggleActive = useCallback(
    (id: number, currentActive: boolean) => {
      if (data?.some((item) => item.id === id && item.accountStatus !== 'active' && item.accountStatus !== undefined)) {
        return;
      }
      if (currentActive) {
        setBlockTargetId(id);
        setBlockModalOpen(true);
      } else {
        void handleSave(id, 'isActive', 'true');
      }
    },
    [data, handleSave],
  );

  const handleConfirmBlock = useCallback(
    (reason: string) => {
      if (blockTargetId !== null) {
        void handleSave(blockTargetId, 'isActive', 'false', reason);
      }
    },
    [blockTargetId, handleSave],
  );

  const columns = useMemo(
    () => getUsualTableColumns(handleSave, handleOpenLog, onToggleActive),
    [handleSave, handleOpenLog, onToggleActive],
  );

  const formattedData: UsualTableItem[] = useMemo(
    () =>
      (data ?? []).map((item) => ({
        id: item.id,
        name: item.name ?? null,
        role: item.role ?? null,
        lastName: item.lastName ?? null,
        firstName: item.firstName ?? null,
        patronymic: item.patronymic ?? null,
        region: item.region ?? null,
        email: item.email ?? null,
        phoneNumber: item.phoneNumber ?? null,
        birthDate: item.birthDate ?? null,
        team: item.team ?? null,
        nickname: item.nickname ?? null,
        vkLink: item.vkLink ?? null,
        rate: item.rate ?? null,
        qualification: item.qualification ?? null,
        status: item.status ?? null,
        dateAuth: item.dateAuth ?? null,
        hardwareId: item.hardwareId ?? null,
        isActive: Boolean(item.isActive),
        accountStatus: item.accountStatus,
        deletion: item.deletion ?? null,
        isChanged: Boolean(item.isChanged),
        hasServersCreated: Boolean(item.hasServersCreated),
      })),
    [data],
  );

  const arrayOfName = selectedArray.map((item) => getAccountDisplayName(item));
  const arrayOfId = selectedArray.map((item) => item.id);
  const accountStatuses = selectedArray.map((item) => item.accountStatus);

  return (
    <>
      {isLoading || !formattedData ? (
        <div className={s.loaderWrapper}>
          <Loader className={s.loader} />
        </div>
      ) : (
        <Table data={formattedData} columns={columns} onSelect={onSelect} tableRef={tableRef} />
      )}
      {!!selectedArray.length && (
        <SelectedPopup
          arrayOfName={arrayOfName}
          arrayOfId={arrayOfId}
          accountStatuses={accountStatuses}
          resetSelectedRows={tableRef?.current?.resetSelectedRows}
        />
      )}
      {logModalInfo && (
        <UserChangeLogModal
          userId={logModalInfo.id}
          userName={logModalInfo.name}
          onClose={() => setLogModalInfo(null)}
        />
      )}
      {blockModalOpen && (
        <BlockReasonModal
          open={blockModalOpen}
          onClose={() => setBlockModalOpen(false)}
          onConfirm={handleConfirmBlock}
        />
      )}
    </>
  );
};
